import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { PaymentItem } from "../../types/payment";
import { statuses } from "../../constants/statuses";

interface TransactionDetailsModalProps {
  transaction: PaymentItem;
  onClose: () => void;
}

export const TransactionDetailsModal: React.FC<
  TransactionDetailsModalProps
> = ({ transaction, onClose }) => {
  const [show, setShow] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setShow(true);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClose = () => {
    setShow(false);
    timerRef.current = setTimeout(onClose, 300);
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();

  const renderField = (
    label: string,
    value?: string | number | null,
    className?: string
  ) => {
    if (value === undefined || value === null || value === "NULL") return null;
    return (
      <div className="flex flex-col">
        <span className="text-gray-500 text-xs uppercase">{label}</span>
        <span className={`font-mono ${className}`}>{value}</span>
      </div>
    );
  };

  const statusConfig = statuses.find((s) => s.value === transaction.status) || {
    color: "bg-gray-500 text-white",
    label: transaction.status,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-center items-center">
      <div
        className={`w-full sm:max-w-lg sm:rounded-lg bg-[#0d0d0d] text-white p-6 max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-700 relative transform transition-all duration-300 ease-out ${
          show
            ? "opacity-100 translate-y-0 sm:scale-100"
            : "opacity-0 translate-y-full sm:scale-95"
        }`}
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1 rounded hover:bg-gray-800"
        >
          <X size={20} />
        </button>

        <h2 className="text-2xl font-bold mb-6 text-center tracking-wider">
          Transaction Details
        </h2>

        <div className="grid grid-cols-2 gap-4">
          {renderField("ID", transaction.id)}

          <div className="flex flex-col">
            <span className="text-gray-500 text-xs uppercase">Status</span>
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  statusConfig.color.split(" ")[0]
                }`}
              ></span>
              <span
                className={`text-xs font-medium px-2 py-1 rounded ${statusConfig.color}`}
              >
                {statusConfig.label}
              </span>
            </div>
          </div>
          {renderField("External Id", transaction.externalProviderId)}
          {renderField("Creation Date", formatDate(transaction.creationTime))}
          {renderField("Last Modified", formatDate(transaction.updateTime))}
          {renderField(
            "Fiat Amount",
            `-${transaction.amount.toFixed(2)} ${transaction.fiatCurrency}`,
            "text-red-500"
          )}
          {renderField(
            "Crypto Amount",
            `+${transaction.amountInCryptoOurRate.toFixed(4)} ${
              transaction.cryptoCurrency
            }`,
            "text-green-500"
          )}
          {renderField(
            "After Commission",
            `+${transaction.amountInCryptoOurRateAfterOurFullCommission.toFixed(
              4
            )} ${transaction.cryptoCurrency}`,
            "text-green-400"
          )}
          {renderField(
            "Commission %",
            `${transaction.providerCommissionInPercent}%`,
            "text-yellow-400"
          )}
          {renderField("Rate", transaction.ourRate, "text-blue-400")}
          {renderField("Callback Code", transaction.callbackCode)}
          {renderField("Payment Type", transaction.payMethodType)}
          {renderField("Bank Name", transaction.bankSimpleName)}
          {renderField("Phone Number", transaction.phoneNumber)}
          {renderField("Card Number", transaction.cardNumber)}
          {renderField("Account Number", transaction.accountNumber)}
          {renderField("Recipient", transaction.recipient)}
        </div>
      </div>
    </div>
  );
};
